import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { 
  Plus, 
  User, 
  Mail, 
  Phone, 
  GripVertical,
  Edit2,
  Trash2,
  X,
  ChevronRight,
  Users,
  TrendingUp,
  Target,
  UserCheck,
  HandHeart,
  Clock,
  Search,
  Filter,
  History,
  CheckSquare,
  Calendar,
  MessageSquare,
  PhoneCall,
  Video,
  FileText,
  AlertCircle,
  CheckCircle,
  Circle,
  ArrowRight,
  Bell,
  ChevronDown,
  ChevronUp,
  Upload,
  FileSpreadsheet,
  Send,
  Copy,
  Eye,
  MailCheck,
  MailX,
  ArrowUpDown,
  Square,
  CheckCheck
} from 'lucide-react';

const Marketing = () => {
  const { API, user } = useContext(AuthContext);
  const [prospectos, setProspectos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [estadisticas, setEstadisticas] = useState(null);
  const [usuariosMarketing, setUsuariosMarketing] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingProspecto, setEditingProspecto] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [draggedItem, setDraggedItem] = useState(null);
  
  // Estado para vista detallada del prospecto
  const [selectedProspecto, setSelectedProspecto] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [historial, setHistorial] = useState([]);
  const [tareas, setTareas] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  
  // Estado para modales de historial y tareas
  const [showHistorialModal, setShowHistorialModal] = useState(false);
  const [showTareaModal, setShowTareaModal] = useState(false);
  
  // Estado para tareas pendientes (dashboard)
  const [tareasPendientes, setTareasPendientes] = useState(null);
  const [showTareasPanel, setShowTareasPanel] = useState(false);
  
  // Estado para importación CSV
  const [showImportModal, setShowImportModal] = useState(false);
  
  // Estado para Campañas de Email (Fase 3)
  const [activeTab, setActiveTab] = useState('dashboard'); // dashboard, pipeline, campanas
  const [campanas, setCampanas] = useState([]);
  const [showCampanaModal, setShowCampanaModal] = useState(false);
  const [editingCampana, setEditingCampana] = useState(null);
  const [loadingCampanas, setLoadingCampanas] = useState(false);
  
  // Estado para selección múltiple y ordenamiento
  const [selectedProspectos, setSelectedProspectos] = useState([]);
  const [sortOrder, setSortOrder] = useState('fecha'); // fecha, nombre

  // Estado para Dashboard de Marketing (Fase 4)
  const [dashboardData, setDashboardData] = useState(null);
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  
  // Estado para Resultados de Votación
  const [votacionResultados, setVotacionResultados] = useState(null);
  const [loadingVotos, setLoadingVotos] = useState(false);

  // Configuración de etapas del pipeline
  const ETAPAS = [
    { id: 'interesado', nombre: 'Interesado', color: 'bg-yellow-500', bgLight: 'bg-yellow-50', icon: Target },
    { id: 'inscrito', nombre: 'Inscrito', color: 'bg-green-500', bgLight: 'bg-green-50', icon: TrendingUp },
    { id: 'estudiante', nombre: 'Estudiante', color: 'bg-purple-500', bgLight: 'bg-purple-50', icon: UserCheck },
    { id: 'pastor', nombre: 'Pastores', color: 'bg-blue-500', bgLight: 'bg-blue-50', icon: HandHeart }
  ];

  const FUENTES = [
    { id: 'web', nombre: 'Página Web' },
    { id: 'referido', nombre: 'Referido' },
    { id: 'redes', nombre: 'Redes Sociales' },
    { id: 'evento', nombre: 'Evento' },
    { id: 'consulta', nombre: 'Área de Consultas' },
    { id: 'otro', nombre: 'Otro' }
  ];

  const TIPOS_CONTACTO = [
    { id: 'llamada', nombre: 'Llamada', icon: PhoneCall, color: 'text-blue-500' },
    { id: 'email', nombre: 'Email', icon: Mail, color: 'text-green-500' },
    { id: 'whatsapp', nombre: 'WhatsApp', icon: MessageSquare, color: 'text-emerald-500' },
    { id: 'reunion', nombre: 'Reunión', icon: Video, color: 'text-purple-500' },
    { id: 'nota', nombre: 'Nota', icon: FileText, color: 'text-gray-500' }
  ];

  const RESULTADOS_CONTACTO = [
    { id: 'positivo', nombre: 'Positivo', color: 'bg-green-100 text-green-700' },
    { id: 'negativo', nombre: 'Negativo', color: 'bg-red-100 text-red-700' },
    { id: 'neutro', nombre: 'Neutro', color: 'bg-gray-100 text-gray-700' },
    { id: 'pendiente', nombre: 'Pendiente', color: 'bg-yellow-100 text-yellow-700' }
  ];

  const PRIORIDADES = [
    { id: 'alta', nombre: 'Alta', color: 'bg-red-100 text-red-700' },
    { id: 'media', nombre: 'Media', color: 'bg-yellow-100 text-yellow-700' },
    { id: 'baja', nombre: 'Baja', color: 'bg-green-100 text-green-700' }
  ];

  useEffect(() => {
    loadData();
    loadTareasPendientes();
    loadCampanas();
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    setLoadingDashboard(true);
    try {
      const response = await axios.get(`${API}/marketing/dashboard`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.data.success) {
        setDashboardData(response.data.dashboard);
      }
    } catch (error) {
      console.error('Error cargando dashboard:', error);
    } finally {
      setLoadingDashboard(false);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      // Cargar cada endpoint por separado para identificar cuál falla
      let prospectosData = [];
      let estadisticasData = {};
      let usuariosData = [];

      try {
        const prospectosRes = await axios.get(`${API}/marketing/prospectos`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (prospectosRes.data?.success) {
          prospectosData = prospectosRes.data.prospectos || [];
        }
      } catch (e) {
        console.error('Error cargando prospectos:', e);
      }

      try {
        const estadisticasRes = await axios.get(`${API}/marketing/estadisticas`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (estadisticasRes.data?.success) {
          estadisticasData = estadisticasRes.data.estadisticas || {};
        }
      } catch (e) {
        console.error('Error cargando estadísticas:', e);
      }

      try {
        const usuariosRes = await axios.get(`${API}/marketing/usuarios-con-acceso`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (usuariosRes.data?.success) {
          usuariosData = usuariosRes.data.usuarios || [];
        }
      } catch (e) {
        console.error('Error cargando usuarios:', e);
      }

      setProspectos(prospectosData);
      setEstadisticas(estadisticasData);
      setUsuariosMarketing(usuariosData);
      
    } catch (error) {
      console.error('Error cargando datos:', error);
      toast.error('Error al cargar datos de marketing');
    } finally {
      setLoading(false);
    }
  };

  const loadTareasPendientes = async () => {
    try {
      const response = await axios.get(`${API}/marketing/tareas-pendientes`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.data.success) {
        setTareasPendientes(response.data);
      }
    } catch (error) {
      console.error('Error cargando tareas pendientes:', error);
    }
  };

  const loadCampanas = async () => {
    setLoadingCampanas(true);
    try {
      const response = await axios.get(`${API}/marketing/campanas`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.data.success) {
        setCampanas(response.data.campanas);
      }
    } catch (error) {
      console.error('Error cargando campañas:', error);
    } finally {
      setLoadingCampanas(false);
    }
  };

  // Cargar resultados de votación de una campaña
  const loadVotosResultados = async (campanaId) => {
    setLoadingVotos(true);
    try {
      const response = await axios.get(`${API}/marketing/campanas/${campanaId}/votos`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.data.success) {
        setVotacionResultados(response.data);
      }
    } catch (error) {
      console.error('Error cargando votos:', error);
      toast.error('Error al cargar resultados de votación');
    } finally {
      setLoadingVotos(false);
    }
  };

  const loadProspectoDetail = async (prospecto) => {
    setSelectedProspecto(prospecto);
    setShowDetailModal(true);
    setLoadingDetail(true);
    
    try {
      const [historialRes, tareasRes] = await Promise.all([
        axios.get(`${API}/marketing/prospectos/${prospecto.id}/historial`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${API}/marketing/prospectos/${prospecto.id}/tareas`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
      ]);

      if (historialRes.data.success) {
        setHistorial(historialRes.data.historial);
      }
      if (tareasRes.data.success) {
        setTareas(tareasRes.data.tareas);
      }
    } catch (error) {
      console.error('Error cargando detalle:', error);
      toast.error('Error al cargar detalle del prospecto');
    } finally {
      setLoadingDetail(false);
    }
  };

  // Filtrar prospectos por búsqueda
  const prospectosFiltrados = prospectos.filter(p => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      p.nombre?.toLowerCase().includes(term) ||
      p.apellido?.toLowerCase().includes(term) ||
      p.email?.toLowerCase().includes(term) ||
      p.telefono?.includes(term)
    );
  });

  // Agrupar prospectos por etapa con ordenamiento
  const prospectosPorEtapa = (etapaId) => {
    let filtered = prospectosFiltrados.filter(p => p.etapa === etapaId);
    
    // Aplicar ordenamiento solo en la etapa "interesado"
    if (etapaId === 'interesado' && sortOrder === 'nombre') {
      filtered = filtered.sort((a, b) => {
        const nombreA = `${a.nombre || ''} ${a.apellido || ''}`.toLowerCase();
        const nombreB = `${b.nombre || ''} ${b.apellido || ''}`.toLowerCase();
        return nombreA.localeCompare(nombreB);
      });
    }
    
    return filtered;
  };

  // Drag and Drop handlers
  const handleDragStart = (e, prospecto) => {
    setDraggedItem(prospecto);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, nuevaEtapa) => {
    e.preventDefault();
    if (!draggedItem || draggedItem.etapa === nuevaEtapa) {
      setDraggedItem(null);
      return;
    }

    try {
      const response = await axios.patch(
        `${API}/marketing/prospectos/${draggedItem.id}/etapa?etapa=${nuevaEtapa}`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }}
      );

      if (response.data.success) {
        setProspectos(prev => prev.map(p => 
          p.id === draggedItem.id ? { ...p, etapa: nuevaEtapa } : p
        ));
        toast.success(`${draggedItem.nombre} movido a ${ETAPAS.find(e => e.id === nuevaEtapa)?.nombre}`);
        loadData();
      }
    } catch (error) {
      console.error('Error moviendo prospecto:', error);
      toast.error('Error al mover prospecto');
    }
    setDraggedItem(null);
  };

  // Funciones para selección múltiple y borrado en lote
  const handleSelectProspecto = (prospectoId) => {
    setSelectedProspectos(prev => {
      if (prev.includes(prospectoId)) {
        return prev.filter(id => id !== prospectoId);
      } else {
        return [...prev, prospectoId];
      }
    });
  };

  const handleSelectAllInteresados = () => {
    const interesados = prospectosPorEtapa('interesado');
    const allIds = interesados.map(p => p.id);
    
    // Si ya están todos seleccionados, deseleccionar todos
    const allSelected = allIds.every(id => selectedProspectos.includes(id));
    
    if (allSelected) {
      setSelectedProspectos(prev => prev.filter(id => !allIds.includes(id)));
    } else {
      setSelectedProspectos(prev => [...new Set([...prev, ...allIds])]);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedProspectos.length === 0) {
      toast.error('No hay prospectos seleccionados');
      return;
    }
    
    if (!window.confirm(`¿Eliminar ${selectedProspectos.length} prospecto(s) seleccionado(s)?`)) {
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/marketing/prospectos/bulk-delete`,
        { ids: selectedProspectos },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }}
      );
      
      if (response.data.success) {
        toast.success(response.data.message);
        setSelectedProspectos([]);
        loadData();
      }
    } catch (error) {
      console.error('Error eliminando prospectos:', error);
      toast.error('Error al eliminar prospectos');
    }
  };

  const toggleSortOrder = () => {
    setSortOrder(prev => prev === 'fecha' ? 'nombre' : 'fecha');
  };

  // Componente Dashboard de Marketing (Fase 4)
  const DashboardMarketing = ({ data, loading, onRefresh }) => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          <span className="ml-3 text-gray-600">Cargando dashboard...</span>
        </div>
      );
    }

    if (!data) {
      return (
        <div className="text-center py-12 text-gray-500">
          <TrendingUp className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>No hay datos disponibles</p>
          <Button onClick={onRefresh} className="mt-4" variant="outline">
            <i className="fas fa-refresh mr-2" /> Recargar
          </Button>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Métricas principales */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Pipeline */}
          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-600 font-medium">Total Prospectos</p>
                  <p className="text-3xl font-bold text-purple-700">{data.pipeline?.total || 0}</p>
                </div>
                <div className="bg-purple-200 p-3 rounded-full">
                  <Users className="h-6 w-6 text-purple-600" />
                </div>
              </div>
              <p className="text-xs text-purple-500 mt-2">
                Tasa de conversión: {data.pipeline?.tasa_conversion || 0}%
              </p>
            </CardContent>
          </Card>

          {/* Nuevos este mes */}
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600 font-medium">Nuevos (Mes)</p>
                  <p className="text-3xl font-bold text-green-700">{data.registros?.nuevos_mes || 0}</p>
                </div>
                <div className="bg-green-200 p-3 rounded-full">
                  <UserCheck className="h-6 w-6 text-green-600" />
                </div>
              </div>
              <p className="text-xs text-green-500 mt-2">
                Esta semana: {data.registros?.nuevos_semana || 0}
              </p>
            </CardContent>
          </Card>

          {/* Estudiantes activos */}
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600 font-medium">Estudiantes Activos</p>
                  <p className="text-3xl font-bold text-blue-700">{data.retencion?.estudiantes_activos || 0}</p>
                </div>
                <div className="bg-blue-200 p-3 rounded-full">
                  <Target className="h-6 w-6 text-blue-600" />
                </div>
              </div>
              <p className="text-xs text-blue-500 mt-2">
                De {data.retencion?.estudiantes_total || 0} totales ({data.retencion?.tasa_actividad || 0}%)
              </p>
            </CardContent>
          </Card>

          {/* Emails */}
          <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-orange-600 font-medium">Campañas Enviadas</p>
                  <p className="text-3xl font-bold text-orange-700">{data.campanas?.enviadas || 0}</p>
                </div>
                <div className="bg-orange-200 p-3 rounded-full">
                  <Mail className="h-6 w-6 text-orange-600" />
                </div>
              </div>
              <p className="text-xs text-orange-500 mt-2">
                Tasa apertura: {data.campanas?.tasa_apertura || 0}%
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Segunda fila: Pipeline detallado y Top Referidores */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Pipeline por etapa */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Target className="h-5 w-5 text-purple-600" />
                Pipeline por Etapa
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <span>Interesados</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-32 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-yellow-500 h-2 rounded-full" 
                        style={{ width: `${data.pipeline?.total ? (data.pipeline.interesados / data.pipeline.total * 100) : 0}%` }}
                      ></div>
                    </div>
                    <span className="font-bold w-8 text-right">{data.pipeline?.interesados || 0}</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    <span>Inscritos</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-32 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-green-500 h-2 rounded-full" 
                        style={{ width: `${data.pipeline?.total ? (data.pipeline.inscritos / data.pipeline.total * 100) : 0}%` }}
                      ></div>
                    </div>
                    <span className="font-bold w-8 text-right">{data.pipeline?.inscritos || 0}</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                    <span>Estudiantes</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-32 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-purple-500 h-2 rounded-full" 
                        style={{ width: `${data.pipeline?.total ? (data.pipeline.estudiantes / data.pipeline.total * 100) : 0}%` }}
                      ></div>
                    </div>
                    <span className="font-bold w-8 text-right">{data.pipeline?.estudiantes || 0}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Top Referidores */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="h-5 w-5 text-green-600" />
                Top Referidores
              </CardTitle>
            </CardHeader>
            <CardContent>
              {data.referidos?.top_referidores?.length > 0 ? (
                <div className="space-y-3">
                  {data.referidos.top_referidores.map((ref, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                          index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : index === 2 ? 'bg-orange-400' : 'bg-gray-300'
                        }`}>
                          {index + 1}
                        </div>
                        <div>
                          <p className="font-medium text-sm">{ref.nombre}</p>
                          <p className="text-xs text-gray-500">Código: {ref.codigo}</p>
                        </div>
                      </div>
                      <Badge className="bg-green-100 text-green-700">
                        {ref.referidos} referidos
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No hay referidores aún</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Tercera fila: Retención y Prospectos recientes */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Retención */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <UserCheck className="h-5 w-5 text-blue-600" />
                Retención de Estudiantes
              </CardTitle>
              <CardDescription>Actividad en los últimos 30 días</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-center gap-8">
                <div className="text-center">
                  <div className="w-24 h-24 rounded-full border-8 border-green-500 flex items-center justify-center mb-2">
                    <span className="text-2xl font-bold text-green-600">{data.retencion?.estudiantes_activos || 0}</span>
                  </div>
                  <p className="text-sm text-gray-600">Activos</p>
                </div>
                <div className="text-center">
                  <div className="w-24 h-24 rounded-full border-8 border-red-300 flex items-center justify-center mb-2">
                    <span className="text-2xl font-bold text-red-400">{data.retencion?.estudiantes_inactivos || 0}</span>
                  </div>
                  <p className="text-sm text-gray-600">Inactivos</p>
                </div>
              </div>
              {data.retencion?.estudiantes_inactivos > 0 && (
                <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                  <p className="text-sm text-yellow-700 flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" />
                    {data.retencion.estudiantes_inactivos} estudiantes no han ingresado en 30 días
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Prospectos recientes */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="h-5 w-5 text-purple-600" />
                Prospectos Recientes
              </CardTitle>
            </CardHeader>
            <CardContent>
              {data.prospectos_recientes?.length > 0 ? (
                <div className="space-y-2">
                  {data.prospectos_recientes.map((prospecto, index) => (
                    <div key={index} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                          <User className="h-4 w-4 text-purple-600" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{prospecto.nombre}</p>
                          <p className="text-xs text-gray-500">{prospecto.email}</p>
                        </div>
                      </div>
                      <Badge className={`text-xs ${
                        prospecto.etapa === 'interesado' ? 'bg-yellow-100 text-yellow-700' :
                        prospecto.etapa === 'inscrito' ? 'bg-green-100 text-green-700' :
                        prospecto.etapa === 'pastor' ? 'bg-blue-100 text-blue-700' :
                        'bg-purple-100 text-purple-700'
                      }`}>
                        {prospecto.etapa}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No hay prospectos recientes</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Cuarta fila: Email Tracking */}
        {data.email_tracking && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Mail className="h-5 w-5 text-indigo-600" />
                Tracking de Emails
              </CardTitle>
              <CardDescription>Monitoreo de apertura de todos los emails del sistema</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="text-center p-4 bg-indigo-50 rounded-lg">
                  <p className="text-2xl font-bold text-indigo-700">{data.email_tracking.total_enviados || 0}</p>
                  <p className="text-sm text-indigo-600">Total Enviados</p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-700">{data.email_tracking.total_abiertos || 0}</p>
                  <p className="text-sm text-green-600">Abiertos</p>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <p className="text-2xl font-bold text-blue-700">{data.email_tracking.tasa_apertura || 0}%</p>
                  <p className="text-sm text-blue-600">Tasa Apertura</p>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <p className="text-2xl font-bold text-purple-700">{data.email_tracking.ultimos_7_dias?.enviados || 0}</p>
                  <p className="text-sm text-purple-600">Últimos 7 días</p>
                </div>
              </div>

              {/* Por tipo de email */}
              {data.email_tracking.por_tipo && Object.keys(data.email_tracking.por_tipo).length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Por tipo de email:</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {Object.entries(data.email_tracking.por_tipo).map(([tipo, stats]) => (
                      <div key={tipo} className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500 capitalize">{tipo}</p>
                        <p className="font-semibold">{stats.abiertos}/{stats.total}</p>
                        <p className="text-xs text-green-600">{stats.tasa}% apertura</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Últimos abiertos */}
              {data.email_tracking.ultimos_abiertos?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Últimos emails abiertos:</h4>
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {data.email_tracking.ultimos_abiertos.map((email, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-green-50 rounded-lg text-sm">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                          <span className="font-medium truncate">{email.destinatario}</span>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="text-gray-500 truncate max-w-[150px] hidden sm:inline">{email.asunto}</span>
                          <Badge className="bg-gray-100 text-gray-600 text-xs">{email.tipo || 'sistema'}</Badge>
                          {email.fecha_apertura && (
                            <span className="text-xs text-gray-400 whitespace-nowrap">
                              {(() => {
                                try {
                                  return new Date(email.fecha_apertura).toLocaleDateString('es-UY', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
                                } catch (e) {
                                  return '';
                                }
                              })()}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Botón de actualizar */}
        <div className="flex justify-center pt-4">
          <Button onClick={onRefresh} variant="outline" className="gap-2">
            <i className="fas fa-sync-alt" /> Actualizar Dashboard
          </Button>
        </div>
      </div>
    );
  };

  // Modal de crear/editar prospecto
  const ProspectoModal = () => {
    const [formData, setFormData] = useState(editingProspecto || {
      nombre: '',
      apellido: '',
      email: '',
      telefono: '',
      etapa: 'interesado',
      fuente: '',
      notas: '',
      asignado_a: ''
    });
    const [saving, setSaving] = useState(false);

    const handleSubmit = async (e) => {
      e.preventDefault();
      if (!formData.nombre) {
        toast.error('El nombre es requerido');
        return;
      }

      setSaving(true);
      try {
        let response;
        if (editingProspecto) {
          response = await axios.put(
            `${API}/marketing/prospectos/${editingProspecto.id}`,
            formData,
            { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }}
          );
        } else {
          response = await axios.post(
            `${API}/marketing/prospectos`,
            formData,
            { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }}
          );
        }

        if (response.data.success) {
          toast.success(editingProspecto ? 'Prospecto actualizado' : 'Prospecto creado');
          setShowModal(false);
          setEditingProspecto(null);
          loadData();
        }
      } catch (error) {
        console.error('Error guardando prospecto:', error);
        toast.error('Error al guardar prospecto');
      } finally {
        setSaving(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between p-4 border-b">
            <h2 className="text-xl font-bold">
              {editingProspecto ? 'Editar Prospecto' : 'Nuevo Prospecto'}
            </h2>
            <Button variant="ghost" size="sm" onClick={() => { setShowModal(false); setEditingProspecto(null); }}>
              <X className="h-5 w-5" />
            </Button>
          </div>

          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nombre *</Label>
                <Input
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  placeholder="Nombre"
                  required
                />
              </div>
              <div>
                <Label>Apellido</Label>
                <Input
                  value={formData.apellido}
                  onChange={(e) => setFormData({ ...formData, apellido: e.target.value })}
                  placeholder="Apellido"
                />
              </div>
            </div>

            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="email@ejemplo.com"
              />
            </div>

            <div>
              <Label>Teléfono</Label>
              <Input
                value={formData.telefono}
                onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                placeholder="+598 99 123 456"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Etapa</Label>
                <select
                  className="w-full p-2 border rounded-md"
                  value={formData.etapa}
                  onChange={(e) => setFormData({ ...formData, etapa: e.target.value })}
                >
                  {ETAPAS.map(e => (
                    <option key={e.id} value={e.id}>{e.nombre}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Fuente</Label>
                <select
                  className="w-full p-2 border rounded-md"
                  value={formData.fuente}
                  onChange={(e) => setFormData({ ...formData, fuente: e.target.value })}
                >
                  <option value="">Seleccionar...</option>
                  {FUENTES.map(f => (
                    <option key={f.id} value={f.id}>{f.nombre}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <Label>Asignado a</Label>
              <select
                className="w-full p-2 border rounded-md"
                value={formData.asignado_a}
                onChange={(e) => setFormData({ ...formData, asignado_a: e.target.value })}
              >
                <option value="">Sin asignar</option>
                {usuariosMarketing.map(u => (
                  <option key={u.id} value={u.id}>{u.nombre}</option>
                ))}
              </select>
            </div>

            <div>
              <Label>Notas</Label>
              <Textarea
                value={formData.notas}
                onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
                placeholder="Notas sobre el prospecto..."
                rows={3}
              />
            </div>

            <div className="flex gap-2 pt-4">
              <Button type="submit" className="flex-1" disabled={saving}>
                {saving ? 'Guardando...' : (editingProspecto ? 'Actualizar' : 'Crear Prospecto')}
              </Button>
              <Button type="button" variant="outline" onClick={() => { setShowModal(false); setEditingProspecto(null); }}>
                Cancelar
              </Button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Modal de detalle del prospecto (Historial + Tareas)
  const ProspectoDetailModal = () => {
    const [activeTab, setActiveTab] = useState('historial');
    
    if (!selectedProspecto) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
            <div>
              <h2 className="text-xl font-bold">{selectedProspecto.nombre} {selectedProspecto.apellido}</h2>
              <div className="flex items-center gap-4 text-sm opacity-90 mt-1">
                {selectedProspecto.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {selectedProspecto.email}</span>}
                {selectedProspecto.telefono && <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {selectedProspecto.telefono}</span>}
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setShowDetailModal(false)} className="text-white hover:bg-white/20">
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Tabs */}
          <div className="flex border-b">
            <button
              className={`flex-1 py-3 px-4 font-medium flex items-center justify-center gap-2 ${activeTab === 'historial' ? 'border-b-2 border-purple-600 text-purple-600' : 'text-gray-500'}`}
              onClick={() => setActiveTab('historial')}
            >
              <History className="h-4 w-4" /> Historial de Contacto
            </button>
            <button
              className={`flex-1 py-3 px-4 font-medium flex items-center justify-center gap-2 ${activeTab === 'tareas' ? 'border-b-2 border-purple-600 text-purple-600' : 'text-gray-500'}`}
              onClick={() => setActiveTab('tareas')}
            >
              <CheckSquare className="h-4 w-4" /> Tareas ({tareas.filter(t => t.estado !== 'completada').length})
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {loadingDetail ? (
              <div className="flex items-center justify-center h-40">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              </div>
            ) : activeTab === 'historial' ? (
              <HistorialTab />
            ) : (
              <TareasTab />
            )}
          </div>
        </div>
      </div>
    );
  };

  // Tab de Historial
  const HistorialTab = () => {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="font-medium text-gray-700">Registro de interacciones</h3>
          <Button size="sm" onClick={() => setShowHistorialModal(true)} className="bg-purple-600 hover:bg-purple-700">
            <Plus className="h-4 w-4 mr-1" /> Agregar Contacto
          </Button>
        </div>

        {historial.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <History className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No hay registros de contacto</p>
            <p className="text-sm">Agrega el primer contacto con este prospecto</p>
          </div>
        ) : (
          <div className="space-y-3">
            {historial.map(item => {
              const tipoInfo = TIPOS_CONTACTO.find(t => t.id === item.tipo);
              const TipoIcon = tipoInfo?.icon || FileText;
              const resultadoInfo = RESULTADOS_CONTACTO.find(r => r.id === item.resultado);
              
              return (
                <div key={item.id} className="bg-gray-50 rounded-lg p-4 border">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-full bg-white ${tipoInfo?.color || 'text-gray-500'}`}>
                      <TipoIcon className="h-4 w-4" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{tipoInfo?.nombre || item.tipo}</span>
                          {resultadoInfo && (
                            <Badge className={resultadoInfo.color}>{resultadoInfo.nombre}</Badge>
                          )}
                        </div>
                        <button
                          onClick={() => handleDeleteHistorial(item.id)}
                          className="text-gray-400 hover:text-red-500"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      <p className="text-gray-600 mt-1">{item.descripcion}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                        <span>{item.fecha_contacto ? new Date(item.fecha_contacto).toLocaleString('es-UY') : '-'}</span>
                        <span>por {item.registrado_por_nombre}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Tab de Tareas
  const TareasTab = () => {
    const tareasPendientes = tareas.filter(t => t.estado !== 'completada');
    const tareasCompletadas = tareas.filter(t => t.estado === 'completada');

    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="font-medium text-gray-700">Tareas y recordatorios</h3>
          <Button size="sm" onClick={() => setShowTareaModal(true)} className="bg-purple-600 hover:bg-purple-700">
            <Plus className="h-4 w-4 mr-1" /> Nueva Tarea
          </Button>
        </div>

        {tareas.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <CheckSquare className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No hay tareas</p>
            <p className="text-sm">Crea una tarea para dar seguimiento a este prospecto</p>
          </div>
        ) : (
          <div className="space-y-4">
            {tareasPendientes.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-2">Pendientes ({tareasPendientes.length})</h4>
                <div className="space-y-2">
                  {tareasPendientes.map(tarea => (
                    <TareaItem key={tarea.id} tarea={tarea} />
                  ))}
                </div>
              </div>
            )}
            
            {tareasCompletadas.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-2">Completadas ({tareasCompletadas.length})</h4>
                <div className="space-y-2 opacity-60">
                  {tareasCompletadas.map(tarea => (
                    <TareaItem key={tarea.id} tarea={tarea} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Item de Tarea
  const TareaItem = ({ tarea }) => {
    const prioridadInfo = PRIORIDADES.find(p => p.id === tarea.prioridad);
    const isVencida = tarea.fecha_vencimiento ? (new Date(tarea.fecha_vencimiento) < new Date() && tarea.estado !== 'completada') : false;
    const isCompletada = tarea.estado === 'completada';

    const handleToggleEstado = async () => {
      const nuevoEstado = isCompletada ? 'pendiente' : 'completada';
      try {
        await axios.put(
          `${API}/marketing/tareas/${tarea.id}`,
          { estado: nuevoEstado },
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }}
        );
        loadProspectoDetail(selectedProspecto);
        loadTareasPendientes();
        toast.success(nuevoEstado === 'completada' ? 'Tarea completada' : 'Tarea reabierta');
      } catch (error) {
        toast.error('Error al actualizar tarea');
      }
    };

    const handleDelete = async () => {
      if (!window.confirm('¿Eliminar esta tarea?')) return;
      try {
        await axios.delete(`${API}/marketing/tareas/${tarea.id}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        loadProspectoDetail(selectedProspecto);
        loadTareasPendientes();
        toast.success('Tarea eliminada');
      } catch (error) {
        toast.error('Error al eliminar tarea');
      }
    };

    return (
      <div className={`bg-white rounded-lg p-3 border ${isVencida ? 'border-red-300 bg-red-50' : ''} ${isCompletada ? 'line-through' : ''}`}>
        <div className="flex items-start gap-3">
          <button onClick={handleToggleEstado} className="mt-0.5">
            {isCompletada ? (
              <CheckCircle className="h-5 w-5 text-green-500" />
            ) : (
              <Circle className="h-5 w-5 text-gray-300 hover:text-purple-500" />
            )}
          </button>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <span className="font-medium">{tarea.titulo}</span>
              <div className="flex items-center gap-2">
                {prioridadInfo && (
                  <Badge className={prioridadInfo.color}>{prioridadInfo.nombre}</Badge>
                )}
                <button onClick={handleDelete} className="text-gray-400 hover:text-red-500">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
            {tarea.descripcion && (
              <p className="text-sm text-gray-600 mt-1">{tarea.descripcion}</p>
            )}
            <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
              <span className={`flex items-center gap-1 ${isVencida ? 'text-red-500 font-medium' : ''}`}>
                <Calendar className="h-3 w-3" />
                {isVencida ? '¡Vencida! ' : ''}
                {tarea.fecha_vencimiento ? new Date(tarea.fecha_vencimiento).toLocaleDateString('es-UY') : '-'}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Modal para agregar contacto al historial
  const HistorialModal = () => {
    const [formData, setFormData] = useState({
      tipo: 'llamada',
      descripcion: '',
      resultado: 'neutro',
      fecha_contacto: new Date().toISOString().slice(0, 16)
    });
    const [saving, setSaving] = useState(false);

    const handleSubmit = async (e) => {
      e.preventDefault();
      if (!formData.descripcion) {
        toast.error('La descripción es requerida');
        return;
      }

      setSaving(true);
      try {
        const response = await axios.post(
          `${API}/marketing/prospectos/${selectedProspecto.id}/historial`,
          formData,
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }}
        );

        if (response.data.success) {
          toast.success('Contacto registrado');
          setShowHistorialModal(false);
          loadProspectoDetail(selectedProspecto);
        }
      } catch (error) {
        toast.error('Error al registrar contacto');
      } finally {
        setSaving(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
          <div className="flex items-center justify-between p-4 border-b">
            <h2 className="text-lg font-bold">Registrar Contacto</h2>
            <Button variant="ghost" size="sm" onClick={() => setShowHistorialModal(false)}>
              <X className="h-5 w-5" />
            </Button>
          </div>

          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            <div>
              <Label>Tipo de Contacto</Label>
              <div className="grid grid-cols-5 gap-2 mt-2">
                {TIPOS_CONTACTO.map(tipo => {
                  const Icon = tipo.icon;
                  return (
                    <button
                      key={tipo.id}
                      type="button"
                      onClick={() => setFormData({ ...formData, tipo: tipo.id })}
                      className={`p-3 rounded-lg border flex flex-col items-center gap-1 ${
                        formData.tipo === tipo.id ? 'border-purple-500 bg-purple-50' : 'hover:bg-gray-50'
                      }`}
                    >
                      <Icon className={`h-5 w-5 ${tipo.color}`} />
                      <span className="text-xs">{tipo.nombre}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label>Descripción *</Label>
              <Textarea
                value={formData.descripcion}
                onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                placeholder="Describe el contacto realizado..."
                rows={3}
                required
              />
            </div>

            <div>
              <Label>Resultado</Label>
              <div className="grid grid-cols-4 gap-2 mt-2">
                {RESULTADOS_CONTACTO.map(resultado => (
                  <button
                    key={resultado.id}
                    type="button"
                    onClick={() => setFormData({ ...formData, resultado: resultado.id })}
                    className={`p-2 rounded-lg border text-sm ${
                      formData.resultado === resultado.id ? 'border-purple-500 ' + resultado.color : 'hover:bg-gray-50'
                    }`}
                  >
                    {resultado.nombre}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label>Fecha y Hora</Label>
              <Input
                type="datetime-local"
                value={formData.fecha_contacto}
                onChange={(e) => setFormData({ ...formData, fecha_contacto: e.target.value })}
              />
            </div>

            <div className="flex gap-2 pt-4">
              <Button type="submit" className="flex-1 bg-purple-600 hover:bg-purple-700" disabled={saving}>
                {saving ? 'Guardando...' : 'Registrar Contacto'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowHistorialModal(false)}>
                Cancelar
              </Button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Modal para crear tarea
  const TareaModal = () => {
    const [formData, setFormData] = useState({
      titulo: '',
      descripcion: '',
      fecha_vencimiento: new Date(Date.now() + 86400000).toISOString().slice(0, 10), // Mañana
      prioridad: 'media',
      asignado_a: user?.id || ''
    });
    const [saving, setSaving] = useState(false);

    const handleSubmit = async (e) => {
      e.preventDefault();
      if (!formData.titulo) {
        toast.error('El título es requerido');
        return;
      }

      setSaving(true);
      try {
        const response = await axios.post(
          `${API}/marketing/prospectos/${selectedProspecto.id}/tareas`,
          formData,
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }}
        );

        if (response.data.success) {
          toast.success('Tarea creada');
          setShowTareaModal(false);
          loadProspectoDetail(selectedProspecto);
          loadTareasPendientes();
        }
      } catch (error) {
        toast.error('Error al crear tarea');
      } finally {
        setSaving(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
          <div className="flex items-center justify-between p-4 border-b">
            <h2 className="text-lg font-bold">Nueva Tarea</h2>
            <Button variant="ghost" size="sm" onClick={() => setShowTareaModal(false)}>
              <X className="h-5 w-5" />
            </Button>
          </div>

          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            <div>
              <Label>Título *</Label>
              <Input
                value={formData.titulo}
                onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                placeholder="Ej: Llamar para seguimiento"
                required
              />
            </div>

            <div>
              <Label>Descripción</Label>
              <Textarea
                value={formData.descripcion}
                onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                placeholder="Detalles adicionales..."
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Fecha Vencimiento</Label>
                <Input
                  type="date"
                  value={formData.fecha_vencimiento}
                  onChange={(e) => setFormData({ ...formData, fecha_vencimiento: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Prioridad</Label>
                <select
                  className="w-full p-2 border rounded-md"
                  value={formData.prioridad}
                  onChange={(e) => setFormData({ ...formData, prioridad: e.target.value })}
                >
                  {PRIORIDADES.map(p => (
                    <option key={p.id} value={p.id}>{p.nombre}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <Label>Asignado a</Label>
              <select
                className="w-full p-2 border rounded-md"
                value={formData.asignado_a}
                onChange={(e) => setFormData({ ...formData, asignado_a: e.target.value })}
              >
                {usuariosMarketing.map(u => (
                  <option key={u.id} value={u.id}>{u.nombre}</option>
                ))}
              </select>
            </div>

            <div className="flex gap-2 pt-4">
              <Button type="submit" className="flex-1 bg-purple-600 hover:bg-purple-700" disabled={saving}>
                {saving ? 'Guardando...' : 'Crear Tarea'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowTareaModal(false)}>
                Cancelar
              </Button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const handleDeleteHistorial = async (contactoId) => {
    if (!window.confirm('¿Eliminar este registro?')) return;
    try {
      await axios.delete(`${API}/marketing/historial/${contactoId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      loadProspectoDetail(selectedProspecto);
      toast.success('Registro eliminado');
    } catch (error) {
      toast.error('Error al eliminar registro');
    }
  };

  // Card de prospecto en el pipeline
  const ProspectoCard = ({ prospecto, showCheckbox = false }) => {
    const isSelected = selectedProspectos.includes(prospecto.id);
    
    const handleDelete = async () => {
      if (!window.confirm(`¿Eliminar a ${prospecto.nombre}?`)) return;
      
      try {
        const response = await axios.delete(
          `${API}/marketing/prospectos/${prospecto.id}`,
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }}
        );
        if (response.data.success) {
          toast.success('Prospecto eliminado');
          loadData();
        }
      } catch (error) {
        toast.error('Error al eliminar');
      }
    };

    return (
      <div
        draggable={!showCheckbox}
        onDragStart={(e) => !showCheckbox && handleDragStart(e, prospecto)}
        className={`bg-white rounded-lg shadow-sm border p-3 cursor-grab hover:shadow-md transition-shadow group ${isSelected ? 'ring-2 ring-purple-500 bg-purple-50' : ''}`}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            {showCheckbox ? (
              <button 
                onClick={() => handleSelectProspecto(prospecto.id)}
                className="p-0.5 hover:bg-purple-100 rounded"
              >
                {isSelected ? (
                  <CheckSquare className="h-5 w-5 text-purple-600" />
                ) : (
                  <Square className="h-5 w-5 text-gray-400" />
                )}
              </button>
            ) : (
              <GripVertical className="h-4 w-4 text-gray-400 opacity-0 group-hover:opacity-100" />
            )}
            <div 
              className="cursor-pointer"
              onClick={() => loadProspectoDetail(prospecto)}
            >
              <p className="font-medium text-gray-900 hover:text-purple-600">{prospecto.nombre} {prospecto.apellido}</p>
              {prospecto.email && (
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <Mail className="h-3 w-3" /> {prospecto.email}
                </p>
              )}
              {prospecto.telefono && (
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <Phone className="h-3 w-3" /> {prospecto.telefono}
                </p>
              )}
              {prospecto.nombre_iglesia && (
                <p className="text-xs text-cyan-600 flex items-center gap-1 mt-1">
                  <i className="fas fa-church text-xs"></i> {prospecto.nombre_iglesia}
                </p>
              )}
              {prospecto.nombre_pastor && (
                <p className="text-xs text-cyan-600 flex items-center gap-1">
                  <i className="fas fa-user-tie text-xs"></i> {prospecto.nombre_pastor}
                </p>
              )}
            </div>
          </div>
          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={() => loadProspectoDetail(prospecto)}
              className="p-1 hover:bg-purple-100 rounded"
              title="Ver detalle"
            >
              <History className="h-3.5 w-3.5 text-purple-500" />
            </button>
            <button
              onClick={() => { setEditingProspecto(prospecto); setShowModal(true); }}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <Edit2 className="h-3.5 w-3.5 text-gray-500" />
            </button>
            <button
              onClick={handleDelete}
              className="p-1 hover:bg-red-100 rounded"
            >
              <Trash2 className="h-3.5 w-3.5 text-red-500" />
            </button>
          </div>
        </div>
        
        {prospecto.fuente && (
          <Badge variant="outline" className="mt-2 text-xs">
            {FUENTES.find(f => f.id === prospecto.fuente)?.nombre || prospecto.fuente}
          </Badge>
        )}
        
        {prospecto.notas && (
          <p className="text-xs text-gray-500 mt-2 line-clamp-2">{prospecto.notas}</p>
        )}
        
        <div className="flex items-center justify-between mt-2 pt-2 border-t">
          <span className="text-xs text-gray-400 flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {prospecto.fecha_creacion ? new Date(prospecto.fecha_creacion).toLocaleDateString('es-UY') : '-'}
          </span>
          {prospecto.asignado_a && (
            <span className="text-xs text-gray-500">
              {usuariosMarketing.find(u => u.id === prospecto.asignado_a)?.nombre?.split(' ')[0] || 'Asignado'}
            </span>
          )}
        </div>
      </div>
    );
  };

  // Panel de Tareas Pendientes
  const TareasPendientesPanel = () => {
    if (!tareasPendientes) return null;

    const { vencidas, hoy, proximas, resumen } = tareasPendientes;
    const totalPendientes = resumen.vencidas + resumen.hoy + resumen.proximas;

    return (
      <Card className="mb-6">
        <CardHeader 
          className="cursor-pointer py-3" 
          onClick={() => setShowTareasPanel(!showTareasPanel)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-full">
                <Bell className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <CardTitle className="text-base">Tareas Pendientes</CardTitle>
                <CardDescription>
                  {resumen.vencidas > 0 && <span className="text-red-500 font-medium">{resumen.vencidas} vencidas</span>}
                  {resumen.vencidas > 0 && resumen.hoy > 0 && ' · '}
                  {resumen.hoy > 0 && <span className="text-yellow-600">{resumen.hoy} para hoy</span>}
                  {(resumen.vencidas > 0 || resumen.hoy > 0) && resumen.proximas > 0 && ' · '}
                  {resumen.proximas > 0 && <span className="text-gray-500">{resumen.proximas} próximas</span>}
                  {totalPendientes === 0 && <span className="text-green-500">Todo al día ✓</span>}
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {totalPendientes > 0 && (
                <Badge className="bg-purple-100 text-purple-700">{totalPendientes}</Badge>
              )}
              {showTareasPanel ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
            </div>
          </div>
        </CardHeader>

        {showTareasPanel && (
          <CardContent className="pt-0">
            <div className="space-y-4">
              {vencidas.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-red-600 mb-2 flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" /> Vencidas
                  </h4>
                  <div className="space-y-2">
                    {vencidas.slice(0, 5).map(tarea => (
                      <TareaQuickItem key={tarea.id} tarea={tarea} />
                    ))}
                  </div>
                </div>
              )}

              {hoy.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-yellow-600 mb-2 flex items-center gap-1">
                    <Clock className="h-4 w-4" /> Para Hoy
                  </h4>
                  <div className="space-y-2">
                    {hoy.slice(0, 5).map(tarea => (
                      <TareaQuickItem key={tarea.id} tarea={tarea} />
                    ))}
                  </div>
                </div>
              )}

              {proximas.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-600 mb-2 flex items-center gap-1">
                    <Calendar className="h-4 w-4" /> Próximas 2 semanas
                  </h4>
                  <div className="space-y-2">
                    {proximas.slice(0, 5).map(tarea => (
                      <TareaQuickItem key={tarea.id} tarea={tarea} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        )}
      </Card>
    );
  };

  // Item rápido de tarea para el panel
  const TareaQuickItem = ({ tarea }) => {
    const handleClick = async () => {
      // Buscar el prospecto y abrir su detalle
      const prospecto = prospectos.find(p => p.id === tarea.prospecto_id);
      if (prospecto) {
        loadProspectoDetail(prospecto);
      }
    };

    return (
      <div 
        className="flex items-center justify-between p-2 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100"
        onClick={handleClick}
      >
        <div className="flex items-center gap-2">
          <CheckSquare className="h-4 w-4 text-gray-400" />
          <div>
            <p className="text-sm font-medium">{tarea.titulo}</p>
            <p className="text-xs text-gray-500">{tarea.prospecto_nombre}</p>
          </div>
        </div>
        <ArrowRight className="h-4 w-4 text-gray-400" />
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="marketing-module">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Marketing</h1>
          <p className="text-gray-500">Pipeline de prospectos y comunicación</p>
        </div>
        <div className="flex gap-2">
          {activeTab === 'pipeline' && (
            <>
              <Button onClick={() => setShowImportModal(true)} variant="outline" className="border-purple-300 text-purple-600 hover:bg-purple-50">
                <Upload className="h-4 w-4 mr-2" /> Importar CSV
              </Button>
              <Button onClick={() => setShowModal(true)} className="bg-purple-600 hover:bg-purple-700">
                <Plus className="h-4 w-4 mr-2" /> Nuevo Prospecto
              </Button>
            </>
          )}
          {activeTab === 'campanas' && (
            <Button onClick={() => { setEditingCampana(null); setShowCampanaModal(true); }} className="bg-purple-600 hover:bg-purple-700">
              <Plus className="h-4 w-4 mr-2" /> Nueva Campaña
            </Button>
          )}
        </div>
      </div>

      {/* Tabs de navegación */}
      <div className="flex border-b overflow-x-auto">
        <button
          className={`py-3 px-6 font-medium flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap ${
            activeTab === 'dashboard' 
              ? 'border-purple-600 text-purple-600' 
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('dashboard')}
        >
          <TrendingUp className="h-4 w-4" /> Dashboard
        </button>
        <button
          className={`py-3 px-6 font-medium flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap ${
            activeTab === 'pipeline' 
              ? 'border-purple-600 text-purple-600' 
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('pipeline')}
        >
          <Users className="h-4 w-4" /> Pipeline
        </button>
        <button
          className={`py-3 px-6 font-medium flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap ${
            activeTab === 'campanas' 
              ? 'border-purple-600 text-purple-600' 
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('campanas')}
        >
          <Mail className="h-4 w-4" /> Campañas
          {campanas.filter(c => c.estado === 'borrador').length > 0 && (
            <Badge className="bg-yellow-100 text-yellow-700 text-xs">
              {campanas.filter(c => c.estado === 'borrador').length}
            </Badge>
          )}
        </button>
      </div>

      {/* Contenido según tab activo */}
      {activeTab === 'dashboard' ? (
        <DashboardMarketing data={dashboardData} loading={loadingDashboard} onRefresh={loadDashboard} />
      ) : activeTab === 'pipeline' ? (
        <>
          {/* Panel de Tareas Pendientes */}
          <TareasPendientesPanel />

      {/* Estadísticas */}
      {estadisticas && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {ETAPAS.map(etapa => {
            const Icon = etapa.icon;
            return (
              <Card key={etapa.id} className={`${etapa.bgLight} border-none`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">{etapa.nombre}</p>
                      <p className="text-2xl font-bold">{estadisticas[etapa.id] || 0}</p>
                    </div>
                    <div className={`p-2 rounded-full ${etapa.color} bg-opacity-20`}>
                      <Icon className={`h-5 w-5 ${etapa.color.replace('bg-', 'text-')}`} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
          <Card className="bg-gray-50 border-none">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total</p>
                  <p className="text-2xl font-bold">{estadisticas.total || 0}</p>
                </div>
                <div className="p-2 rounded-full bg-gray-200">
                  <Users className="h-5 w-5 text-gray-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Búsqueda */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Buscar prospectos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Pipeline Kanban */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 pb-4">
        {ETAPAS.map(etapa => {
          const Icon = etapa.icon;
          const prospectosEtapa = prospectosPorEtapa(etapa.id);
          const isInteresado = etapa.id === 'interesado';
          const selectedInEtapa = isInteresado ? prospectosEtapa.filter(p => selectedProspectos.includes(p.id)).length : 0;
          const allSelectedInEtapa = isInteresado && prospectosEtapa.length > 0 && prospectosEtapa.every(p => selectedProspectos.includes(p.id));
          
          return (
            <div
              key={etapa.id}
              className="min-w-[280px]"
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, etapa.id)}
            >
              <div className={`rounded-t-lg p-3 ${etapa.color} text-white`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4" />
                    <span className="font-medium">{etapa.nombre}</span>
                  </div>
                  <Badge variant="secondary" className="bg-white/20 text-white">
                    {prospectosEtapa.length}
                  </Badge>
                </div>
                
                {/* Controles adicionales solo para "Interesado" */}
                {isInteresado && prospectosEtapa.length > 0 && (
                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/30">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleSelectAllInteresados}
                        className="flex items-center gap-1 text-xs bg-white/20 hover:bg-white/30 px-2 py-1 rounded transition-colors"
                        title={allSelectedInEtapa ? "Deseleccionar todos" : "Seleccionar todos"}
                      >
                        {allSelectedInEtapa ? (
                          <CheckCheck className="h-3 w-3" />
                        ) : (
                          <Square className="h-3 w-3" />
                        )}
                        <span>{allSelectedInEtapa ? 'Deselec.' : 'Selec. todos'}</span>
                      </button>
                      <button
                        onClick={toggleSortOrder}
                        className="flex items-center gap-1 text-xs bg-white/20 hover:bg-white/30 px-2 py-1 rounded transition-colors"
                        title={sortOrder === 'nombre' ? 'Ordenar por fecha' : 'Ordenar alfabéticamente'}
                      >
                        <ArrowUpDown className="h-3 w-3" />
                        <span>{sortOrder === 'nombre' ? 'A-Z' : 'Fecha'}</span>
                      </button>
                    </div>
                    {selectedInEtapa > 0 && (
                      <button
                        onClick={handleDeleteSelected}
                        className="flex items-center gap-1 text-xs bg-red-500/80 hover:bg-red-500 px-2 py-1 rounded transition-colors"
                        title={`Eliminar ${selectedInEtapa} seleccionados`}
                      >
                        <Trash2 className="h-3 w-3" />
                        <span>Eliminar ({selectedInEtapa})</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
              
              <div className={`${etapa.bgLight} rounded-b-lg p-2 min-h-[300px] space-y-2`}>
                {prospectosEtapa.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-32 text-gray-400">
                    <Icon className="h-8 w-8 mb-2 opacity-50" />
                    <p className="text-sm">Sin prospectos</p>
                  </div>
                ) : (
                  prospectosEtapa.map(prospecto => (
                    <ProspectoCard 
                      key={prospecto.id} 
                      prospecto={prospecto} 
                      showCheckbox={isInteresado}
                    />
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Indicador de arrastre */}
      {draggedItem && (
        <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-purple-600 text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2">
          <GripVertical className="h-4 w-4" />
          Arrastra a otra columna para cambiar etapa
        </div>
      )}
        </>
      ) : (
        /* Vista de Campañas de Email */
        <CampanasView />
      )}

      {/* Modales */}
      {showModal && <ProspectoModal />}
      {showDetailModal && <ProspectoDetailModal />}
      {showHistorialModal && <HistorialModal />}
      {showTareaModal && <TareaModal />}
      {showImportModal && <ImportCSVModal />}
      {showCampanaModal && <CampanaModal />}
    </div>
  );
  
  // Vista de Campañas de Email
  function CampanasView() {
    const [previewCampana, setPreviewCampana] = useState(null);
    const [destinatariosPreview, setDestinatariosPreview] = useState(null);
    const [enviando, setEnviando] = useState(false);
    const [showEnviarModal, setShowEnviarModal] = useState(null); // campana a enviar
    const [destinatariosSeleccionados, setDestinatariosSeleccionados] = useState([]);
    const [loadingDestinatarios, setLoadingDestinatarios] = useState(false);
    const [listaDestinatarios, setListaDestinatarios] = useState([]);
    const [verContenidoCampana, setVerContenidoCampana] = useState(null); // Para ver contenido de campañas enviadas

    const handlePreviewDestinatarios = async (campana) => {
      try {
        const response = await axios.get(`${API}/marketing/campanas/${campana.id}/preview-destinatarios`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (response.data.success) {
          setPreviewCampana(campana);
          setDestinatariosPreview(response.data);
        }
      } catch (error) {
        toast.error('Error al cargar preview');
      }
    };

    // Abrir modal de envío con lista de destinatarios
    const abrirModalEnvio = async (campana) => {
      setShowEnviarModal(campana);
      setLoadingDestinatarios(true);
      try {
        const response = await axios.get(`${API}/marketing/campanas/${campana.id}/preview-destinatarios`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (response.data.success) {
          setListaDestinatarios(response.data.prospectos || []);
          // Por defecto todos seleccionados
          setDestinatariosSeleccionados(response.data.prospectos?.map(p => p.id) || []);
        }
      } catch (error) {
        toast.error('Error al cargar destinatarios');
        setShowEnviarModal(null);
      } finally {
        setLoadingDestinatarios(false);
      }
    };

    const toggleDestinatario = (id) => {
      setDestinatariosSeleccionados(prev => 
        prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
      );
    };

    const seleccionarTodos = () => {
      setDestinatariosSeleccionados(listaDestinatarios.map(p => p.id));
    };

    const deseleccionarTodos = () => {
      setDestinatariosSeleccionados([]);
    };

    const handleEnviarCampana = async (campana) => {
      if (!window.confirm(`¿Enviar la campaña "${campana.nombre}" a todos los prospectos del segmento "${campana.segmento}"?`)) {
        return;
      }

      setEnviando(true);
      try {
        const response = await axios.post(`${API}/marketing/campanas/${campana.id}/enviar`, {}, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (response.data.success) {
          toast.success(response.data.message);
          loadCampanas();
          setPreviewCampana(null);
          setDestinatariosPreview(null);
        }
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Error al enviar campaña');
      } finally {
        setEnviando(false);
      }
    };

    // Enviar a seleccionados
    const enviarASeleccionados = async () => {
      if (destinatariosSeleccionados.length === 0) {
        toast.error('Selecciona al menos un destinatario');
        return;
      }
      
      setEnviando(true);
      try {
        let response;
        // Si están todos seleccionados, usar el endpoint original que funciona
        if (destinatariosSeleccionados.length === listaDestinatarios.length) {
          response = await axios.post(
            `${API}/marketing/campanas/${showEnviarModal.id}/enviar`,
            {},
            { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }}
          );
        } else {
          // Si es selección parcial, usar el nuevo endpoint
          response = await axios.post(
            `${API}/marketing/campanas/${showEnviarModal.id}/enviar-seleccionados`,
            { prospecto_ids: destinatariosSeleccionados },
            { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }}
          );
        }
        if (response.data.success) {
          toast.success(response.data.message);
          loadCampanas();
          setShowEnviarModal(null);
          setPreviewCampana(null);
        }
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Error al enviar campaña');
      } finally {
        setEnviando(false);
      }
    };

    const handleDuplicarCampana = async (campana) => {
      try {
        const response = await axios.post(`${API}/marketing/campanas/${campana.id}/duplicar`, {}, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (response.data.success) {
          toast.success('Campaña duplicada');
          loadCampanas();
        }
      } catch (error) {
        toast.error('Error al duplicar campaña');
      }
    };

    const handleEliminarCampana = async (campana) => {
      if (!window.confirm(`¿Eliminar la campaña "${campana.nombre}"?`)) return;
      
      try {
        await axios.delete(`${API}/marketing/campanas/${campana.id}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        toast.success('Campaña eliminada');
        loadCampanas();
      } catch (error) {
        toast.error('Error al eliminar campaña');
      }
    };

    const campanasBorrador = campanas.filter(c => c.estado === 'borrador');
    const campanasEnviadas = campanas.filter(c => c.estado === 'enviada');

    if (loadingCampanas) {
      return (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Borradores */}
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <FileText className="h-5 w-5 text-yellow-500" />
            Borradores ({campanasBorrador.length})
          </h3>
          
          {campanasBorrador.length === 0 ? (
            <Card className="bg-gray-50">
              <CardContent className="p-8 text-center text-gray-500">
                <Mail className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No hay campañas en borrador</p>
                <p className="text-sm">Crea una nueva campaña para empezar</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {campanasBorrador.map(campana => (
                <Card key={campana.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className="font-semibold text-gray-900">{campana.nombre}</h4>
                        <p className="text-sm text-gray-500">Asunto: {campana.asunto}</p>
                      </div>
                      <Badge className="bg-yellow-100 text-yellow-700">Borrador</Badge>
                    </div>
                    
                    <div className="text-xs text-gray-400 mb-3">
                      Segmento: <span className="font-medium text-gray-600">{campana.segmento === 'todos' ? 'Todos' : campana.segmento}</span>
                    </div>
                    
                    <div className="flex gap-2 flex-wrap">
                      <Button size="sm" variant="outline" onClick={() => { setEditingCampana(campana); setShowCampanaModal(true); }}>
                        <Edit2 className="h-3 w-3 mr-1" /> Editar
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handlePreviewDestinatarios(campana)}>
                        <Eye className="h-3 w-3 mr-1" /> Preview
                      </Button>
                      <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => abrirModalEnvio(campana)} disabled={enviando}>
                        <Send className="h-3 w-3 mr-1" /> Enviar
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleEliminarCampana(campana)}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Enviadas */}
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <MailCheck className="h-5 w-5 text-green-500" />
            Campañas Enviadas ({campanasEnviadas.length})
          </h3>
          
          {campanasEnviadas.length === 0 ? (
            <Card className="bg-gray-50">
              <CardContent className="p-6 text-center text-gray-500">
                <p className="text-sm">No hay campañas enviadas aún</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {campanasEnviadas.map(campana => (
                <Card key={campana.id} className="bg-green-50/50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                      <div>
                        <h4 className="font-semibold text-gray-900">{campana.nombre}</h4>
                        <p className="text-sm text-gray-500">Asunto: {campana.asunto}</p>
                        <p className="text-xs text-gray-400">
                          Enviada el {campana.fecha_envio_fin ? new Date(campana.fecha_envio_fin).toLocaleString('es-UY') : '-'} por {campana.enviado_por_nombre || 'Desconocido'}
                        </p>
                        {campana.habilitar_votacion && (
                          <Badge className="mt-1 bg-purple-100 text-purple-700">
                            📊 Con votación
                          </Badge>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <div className="text-center">
                          <p className="text-2xl font-bold text-green-600">{campana.estadisticas?.enviados || 0}</p>
                          <p className="text-xs text-gray-500">Enviados</p>
                        </div>
                        {campana.estadisticas?.fallidos > 0 && (
                          <div className="text-center">
                            <p className="text-2xl font-bold text-red-500">{campana.estadisticas.fallidos}</p>
                            <p className="text-xs text-gray-500">Fallidos</p>
                          </div>
                        )}
                        
                        {/* Mostrar votos si la campaña tiene votación */}
                        {campana.habilitar_votacion && (
                          <>
                            <div className="text-center">
                              <p className="text-2xl font-bold text-green-600">{campana.estadisticas?.votos_si || 0}</p>
                              <p className="text-xs text-gray-500">Votaron SÍ</p>
                            </div>
                            <div className="text-center">
                              <p className="text-2xl font-bold text-red-500">{campana.estadisticas?.votos_no || 0}</p>
                              <p className="text-xs text-gray-500">Votaron NO</p>
                            </div>
                          </>
                        )}
                        
                        <Button size="sm" variant="outline" onClick={() => setVerContenidoCampana(campana)}>
                          <Eye className="h-3 w-3 mr-1" /> Ver Contenido
                        </Button>
                        {campana.habilitar_votacion && (
                          <Button size="sm" variant="outline" className="text-purple-600 border-purple-300" onClick={() => loadVotosResultados(campana.id)}>
                            📊 Ver Votos
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => handleDuplicarCampana(campana)}>
                          <Copy className="h-3 w-3 mr-1" /> Duplicar
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-500 hover:bg-red-50" onClick={() => handleEliminarCampana(campana)}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Modal Preview de Destinatarios */}
        {previewCampana && destinatariosPreview && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
              <div className="flex items-center justify-between p-4 border-b">
                <h2 className="text-lg font-bold">Preview: {previewCampana.nombre}</h2>
                <Button variant="ghost" size="sm" onClick={() => { setPreviewCampana(null); setDestinatariosPreview(null); }}>
                  <X className="h-5 w-5" />
                </Button>
              </div>
              
              <div className="p-4 flex-1 overflow-y-auto">
                <div className="bg-purple-50 rounded-lg p-4 mb-4">
                  <p className="text-lg font-bold text-purple-700">{destinatariosPreview.total} destinatarios</p>
                  <p className="text-sm text-purple-600">Segmento: {destinatariosPreview.segmento === 'todos' ? 'Todos los prospectos' : destinatariosPreview.segmento}</p>
                </div>
                
                <p className="text-sm font-medium text-gray-700 mb-2">Primeros {Math.min(50, destinatariosPreview.prospectos.length)} destinatarios:</p>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {destinatariosPreview.prospectos.map(p => (
                    <div key={p.id} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                      <span>{p.nombre} {p.apellido}</span>
                      <span className="text-gray-500">{p.email}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="p-4 border-t flex gap-2">
                <Button 
                  className="flex-1 bg-green-600 hover:bg-green-700" 
                  onClick={() => { setPreviewCampana(null); abrirModalEnvio(previewCampana); }}
                  disabled={enviando || destinatariosPreview.total === 0}
                >
                  {enviando ? 'Enviando...' : (
                    <>
                      <Send className="h-4 w-4 mr-2" /> Enviar
                    </>
                  )}
                </Button>
                <Button variant="outline" onClick={() => { setPreviewCampana(null); setDestinatariosPreview(null); }}>
                  Cancelar
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Selección de Destinatarios */}
        {showEnviarModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
              <div className="flex items-center justify-between p-4 border-b">
                <h2 className="text-lg font-bold flex items-center gap-2">
                  <Send className="h-5 w-5 text-green-600" />
                  Enviar: {showEnviarModal.nombre}
                </h2>
                <Button variant="ghost" size="sm" onClick={() => setShowEnviarModal(null)}>
                  <X className="h-5 w-5" />
                </Button>
              </div>
              
              {loadingDestinatarios ? (
                <div className="flex items-center justify-center p-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                </div>
              ) : (
                <>
                  <div className="p-4 border-b bg-gray-50">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">
                        {destinatariosSeleccionados.length} de {listaDestinatarios.length} seleccionados
                      </span>
                      <div className="flex gap-2">
                        <button 
                          onClick={seleccionarTodos}
                          className="text-xs text-purple-600 hover:underline"
                        >
                          Todos
                        </button>
                        <span className="text-gray-300">|</span>
                        <button 
                          onClick={deseleccionarTodos}
                          className="text-xs text-gray-500 hover:underline"
                        >
                          Ninguno
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex-1 overflow-y-auto p-2">
                    {listaDestinatarios.length === 0 ? (
                      <p className="text-center text-gray-500 py-8">No hay destinatarios en este segmento</p>
                    ) : (
                      listaDestinatarios.map(p => (
                        <label 
                          key={p.id} 
                          className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={destinatariosSeleccionados.includes(p.id)}
                            onChange={() => toggleDestinatario(p.id)}
                            className="w-4 h-4 text-green-600 rounded"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{p.nombre} {p.apellido}</p>
                            <p className="text-xs text-gray-500 truncate">{p.email}</p>
                          </div>
                        </label>
                      ))
                    )}
                  </div>
                  
                  <div className="p-4 border-t flex gap-2">
                    <Button 
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      onClick={enviarASeleccionados}
                      disabled={enviando || destinatariosSeleccionados.length === 0}
                    >
                      {enviando ? 'Enviando...' : `Enviar a ${destinatariosSeleccionados.length} seleccionados`}
                    </Button>
                    <Button variant="outline" onClick={() => setShowEnviarModal(null)}>
                      Cancelar
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Modal de Ver Contenido de Campaña Enviada */}
        {verContenidoCampana && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
              <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-t-xl">
                <div>
                  <h2 className="text-lg font-bold flex items-center gap-2">
                    <Mail className="h-5 w-5" />
                    {verContenidoCampana.nombre}
                  </h2>
                  <p className="text-sm text-green-100 mt-1">
                    Enviada el {verContenidoCampana.fecha_envio_fin ? new Date(verContenidoCampana.fecha_envio_fin).toLocaleString('es-UY') : '-'}
                  </p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setVerContenidoCampana(null)} className="text-white hover:bg-white/20">
                  <X className="h-5 w-5" />
                </Button>
              </div>
              
              <div className="p-4 flex-1 overflow-y-auto">
                {/* Info de la campaña */}
                <div className="bg-gray-50 rounded-lg p-4 mb-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Asunto:</span>
                    <span className="font-medium">{verContenidoCampana.asunto}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Segmento:</span>
                    <Badge className="bg-purple-100 text-purple-700">
                      {verContenidoCampana.segmento === 'todos' ? 'Todos los prospectos' : verContenidoCampana.segmento}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Enviados:</span>
                    <span className="font-medium text-green-600">{verContenidoCampana.estadisticas?.enviados || 0} emails</span>
                  </div>
                  {verContenidoCampana.estadisticas?.fallidos > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Fallidos:</span>
                      <span className="font-medium text-red-600">{verContenidoCampana.estadisticas.fallidos} emails</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Enviado por:</span>
                    <span className="text-gray-700">{verContenidoCampana.enviado_por_nombre}</span>
                  </div>
                </div>

                {/* Contenido del email */}
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-gray-100 px-4 py-2 border-b">
                    <p className="text-sm font-medium text-gray-600">Contenido del Email:</p>
                  </div>
                  <div 
                    className="p-4 bg-white prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: verContenidoCampana.contenido || '<p class="text-gray-400">Sin contenido</p>' }}
                  />
                </div>
              </div>
              
              <div className="p-4 border-t flex gap-2 justify-end">
                <Button variant="outline" onClick={() => handleDuplicarCampana(verContenidoCampana)}>
                  <Copy className="h-4 w-4 mr-2" /> Duplicar Campaña
                </Button>
                <Button onClick={() => setVerContenidoCampana(null)}>
                  Cerrar
                </Button>
              </div>
            </div>
          </div>
        )}
        
        {/* Modal de Resultados de Votación */}
        {votacionResultados && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
              <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-t-xl">
                <div>
                  <h2 className="text-lg font-bold flex items-center gap-2">
                    📊 Resultados de Votación
                  </h2>
                  <p className="text-sm text-purple-100 mt-1">
                    {votacionResultados.campana}
                  </p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setVotacionResultados(null)} className="text-white hover:bg-white/20">
                  <X className="h-5 w-5" />
                </Button>
              </div>
              
              <div className="p-4 flex-1 overflow-y-auto">
                {loadingVotos ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600 mx-auto"></div>
                    <p className="text-gray-500 mt-2">Cargando votos...</p>
                  </div>
                ) : (
                  <>
                    {/* Pregunta */}
                    <div className="bg-purple-50 rounded-lg p-4 mb-4 text-center">
                      <p className="text-lg font-medium text-purple-800">"{votacionResultados.pregunta}"</p>
                    </div>
                    
                    {/* Resumen de votos */}
                    <div className="grid grid-cols-3 gap-4 mb-6">
                      <div className="bg-green-50 rounded-lg p-4 text-center border border-green-200">
                        <p className="text-4xl font-bold text-green-600">{votacionResultados.resultados?.votos_si || 0}</p>
                        <p className="text-sm text-green-700 mt-1">✅ Votaron SÍ</p>
                        <p className="text-lg font-semibold text-green-600">{votacionResultados.resultados?.porcentaje_si || 0}%</p>
                      </div>
                      <div className="bg-red-50 rounded-lg p-4 text-center border border-red-200">
                        <p className="text-4xl font-bold text-red-600">{votacionResultados.resultados?.votos_no || 0}</p>
                        <p className="text-sm text-red-700 mt-1">❌ Votaron NO</p>
                        <p className="text-lg font-semibold text-red-600">{votacionResultados.resultados?.porcentaje_no || 0}%</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4 text-center border border-gray-200">
                        <p className="text-4xl font-bold text-gray-700">{votacionResultados.resultados?.total_votos || 0}</p>
                        <p className="text-sm text-gray-600 mt-1">📊 Total Votos</p>
                        <p className="text-lg font-semibold text-gray-500">100%</p>
                      </div>
                    </div>
                    
                    {/* Barra de progreso visual */}
                    <div className="mb-6">
                      <div className="h-8 bg-gray-200 rounded-full overflow-hidden flex">
                        <div 
                          className="bg-green-500 h-full flex items-center justify-center text-white text-sm font-bold transition-all duration-500"
                          style={{ width: `${votacionResultados.resultados?.porcentaje_si || 0}%` }}
                        >
                          {votacionResultados.resultados?.porcentaje_si > 10 && `${votacionResultados.resultados?.porcentaje_si}%`}
                        </div>
                        <div 
                          className="bg-red-500 h-full flex items-center justify-center text-white text-sm font-bold transition-all duration-500"
                          style={{ width: `${votacionResultados.resultados?.porcentaje_no || 0}%` }}
                        >
                          {votacionResultados.resultados?.porcentaje_no > 10 && `${votacionResultados.resultados?.porcentaje_no}%`}
                        </div>
                      </div>
                    </div>
                    
                    {/* Lista de votantes */}
                    <div>
                      <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                        👥 Detalle de Votantes ({votacionResultados.votantes?.length || 0})
                      </h4>
                      
                      {votacionResultados.votantes && votacionResultados.votantes.length > 0 ? (
                        <div className="border rounded-lg overflow-hidden">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-100">
                              <tr>
                                <th className="text-left p-3 font-medium">Votante</th>
                                <th className="text-center p-3 font-medium">Voto</th>
                                <th className="text-right p-3 font-medium">Fecha</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y">
                              {votacionResultados.votantes.map((votante, idx) => (
                                <tr key={idx} className="hover:bg-gray-50">
                                  <td className="p-3">
                                    <span className="font-medium">{votante.nombre || votante.email}</span>
                                    {votante.nombre && votante.nombre !== votante.email && (
                                      <span className="text-xs text-gray-400 block">{votante.email}</span>
                                    )}
                                  </td>
                                  <td className="p-3 text-center">
                                    {votante.voto === 'si' ? (
                                      <Badge className="bg-green-100 text-green-700">✅ SÍ</Badge>
                                    ) : (
                                      <Badge className="bg-red-100 text-red-700">❌ NO</Badge>
                                    )}
                                  </td>
                                  <td className="p-3 text-right text-gray-500 text-xs">
                                    {votante.fecha ? new Date(votante.fecha).toLocaleString('es-UY') : '-'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                          <p>Aún no hay votos registrados</p>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
              
              <div className="p-4 border-t flex gap-2 justify-end">
                <Button onClick={() => setVotacionResultados(null)}>
                  Cerrar
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
  
  // Modal de Crear/Editar Campaña
  function CampanaModal() {
    const DRAFT_KEY = 'marketing_email_draft';
    
    // Limpiar contenido de líneas vacías al inicio
    const limpiarContenido = (contenido) => {
      if (!contenido) return '';
      return contenido
        .replace(/^(\s*<br\s*\/?>\s*)+/gi, '')
        .replace(/^(\s*<p>\s*<\/p>\s*)+/gi, '')
        .replace(/^(\s*&nbsp;\s*)+/gi, '')
        .replace(/^\s+/, '')
        .trim();
    };
    
    // Cargar borrador guardado
    const cargarBorrador = () => {
      try {
        const saved = localStorage.getItem(DRAFT_KEY);
        if (saved) {
          return JSON.parse(saved);
        }
      } catch (e) {
        console.error('Error cargando borrador:', e);
      }
      return null;
    };
    
    const [formData, setFormData] = useState(() => {
      if (editingCampana) {
        return {
          ...editingCampana,
          contenido: limpiarContenido(editingCampana.contenido)
        };
      }
      
      // Si no estamos editando, verificar si hay un borrador guardado
      const borrador = cargarBorrador();
      if (borrador && borrador.contenido) {
        // Mostrar confirmación para recuperar
        setTimeout(() => {
          if (window.confirm('Se encontró un borrador guardado. ¿Deseas recuperarlo?')) {
            setFormData(borrador);
            toast.success('Borrador recuperado');
          } else {
            localStorage.removeItem(DRAFT_KEY);
          }
        }, 500);
      }
      
      return {
        nombre: '',
        asunto: '',
        contenido: '',
        tipo_contenido: 'html',
        segmento: 'todos',
        habilitar_votacion: false,
        pregunta_votacion: ''
      };
    });
    const [saving, setSaving] = useState(false);
    const [showInsertModal, setShowInsertModal] = useState(null);
    const [insertData, setInsertData] = useState({ url: '', text: '', alt: '' });
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [lastSaved, setLastSaved] = useState(null);
    const textareaRef = React.useRef(null);
    
    // Marcar cambios sin guardar
    React.useEffect(() => {
      if (formData.nombre || formData.asunto || formData.contenido) {
        setHasUnsavedChanges(true);
      }
    }, [formData]);
    
    // Guardar inmediatamente cuando hay cambios (debounced)
    React.useEffect(() => {
      if (formData.nombre || formData.asunto || formData.contenido) {
        // Guardar después de 3 segundos de inactividad
        const debounceTimer = setTimeout(() => {
          localStorage.setItem(DRAFT_KEY, JSON.stringify(formData));
          setLastSaved(new Date());
          console.log('📝 Borrador guardado (debounce)');
        }, 3000);
        
        return () => clearTimeout(debounceTimer);
      }
    }, [formData]);
    
    // Auto-guardado cada 15 segundos como respaldo
    React.useEffect(() => {
      const autoSaveInterval = setInterval(() => {
        if (formData.nombre || formData.asunto || formData.contenido) {
          localStorage.setItem(DRAFT_KEY, JSON.stringify(formData));
          setLastSaved(new Date());
          console.log('📝 Borrador auto-guardado (interval)');
        }
      }, 15000); // 15 segundos
      
      return () => clearInterval(autoSaveInterval);
    }, [formData]);
    
    // Guardar al perder foco de la ventana o antes de cerrar
    React.useEffect(() => {
      const guardarAntesDeSalir = () => {
        if (formData.nombre || formData.asunto || formData.contenido) {
          localStorage.setItem(DRAFT_KEY, JSON.stringify(formData));
          console.log('📝 Borrador guardado (beforeunload/blur)');
        }
      };
      
      window.addEventListener('beforeunload', guardarAntesDeSalir);
      window.addEventListener('blur', guardarAntesDeSalir);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          guardarAntesDeSalir();
        }
      });
      
      return () => {
        // Guardar al desmontar el componente
        guardarAntesDeSalir();
        window.removeEventListener('beforeunload', guardarAntesDeSalir);
        window.removeEventListener('blur', guardarAntesDeSalir);
      };
    }, [formData]);
    
    // Guardar borrador manualmente
    const guardarBorrador = () => {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(formData));
      setLastSaved(new Date());
      setHasUnsavedChanges(false);
      toast.success('Borrador guardado');
    };
    
    // Función para cerrar con confirmación
    const handleCerrar = () => {
      if (hasUnsavedChanges && (formData.nombre || formData.asunto || formData.contenido)) {
        const confirmar = window.confirm(
          '⚠️ Tienes cambios sin guardar.\n\n' +
          '¿Qué deseas hacer?\n' +
          '• OK = Guardar borrador y cerrar\n' +
          '• Cancelar = Seguir editando'
        );
        if (confirmar) {
          localStorage.setItem(DRAFT_KEY, JSON.stringify(formData));
          toast.info('Borrador guardado automáticamente');
          setShowCampanaModal(false);
          setEditingCampana(null);
        }
        // Si cancela, no hace nada y sigue editando
      } else {
        setShowCampanaModal(false);
        setEditingCampana(null);
      }
    };
    
    // Limpiar borrador al guardar exitosamente
    const limpiarBorrador = () => {
      localStorage.removeItem(DRAFT_KEY);
      setHasUnsavedChanges(false);
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      if (!formData.nombre || !formData.asunto || !formData.contenido) {
        toast.error('Completa todos los campos requeridos');
        return;
      }

      setSaving(true);
      try {
        let response;
        if (editingCampana) {
          response = await axios.put(`${API}/marketing/campanas/${editingCampana.id}`, formData, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });
        } else {
          response = await axios.post(`${API}/marketing/campanas`, formData, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });
        }

        if (response.data.success) {
          toast.success(editingCampana ? 'Campaña actualizada' : 'Campaña creada');
          limpiarBorrador(); // Limpiar borrador al guardar exitosamente
          setShowCampanaModal(false);
          setEditingCampana(null);
          loadCampanas();
        }
      } catch (error) {
        toast.error('Error al guardar campaña');
      } finally {
        setSaving(false);
      }
    };

    // Función para insertar HTML en el textarea
    const insertAtCursor = (htmlCode) => {
      const textarea = textareaRef.current;
      if (!textarea) {
        setFormData({ ...formData, contenido: (formData.contenido || '') + '\n' + htmlCode });
        return;
      }
      
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = formData.contenido || '';
      const before = text.substring(0, start);
      const after = text.substring(end);
      
      const newContent = before + htmlCode + after;
      setFormData({ ...formData, contenido: newContent });
      
      // Restaurar foco y posición del cursor
      setTimeout(() => {
        textarea.focus();
        const newPosition = start + htmlCode.length;
        textarea.selectionStart = textarea.selectionEnd = newPosition;
      }, 100);
    };

    // Insertar imagen
    const handleInsertImage = () => {
      if (!insertData.url) {
        toast.error('Ingresa la URL de la imagen');
        return;
      }
      const html = `<img src="${insertData.url}" alt="${insertData.alt || 'Imagen'}" style="max-width: 100%; height: auto; margin: 10px 0;" />`;
      insertAtCursor(html);
      setShowInsertModal(null);
      setInsertData({ url: '', text: '', alt: '' });
    };

    // Insertar video de YouTube
    const handleInsertVideo = () => {
      if (!insertData.url) {
        toast.error('Ingresa la URL del video de YouTube');
        return;
      }
      // Extraer ID del video - soporta múltiples formatos incluyendo Shorts
      let videoId = '';
      const youtubeRegex = /(?:youtube\.com\/(?:shorts\/|watch\?v=|embed\/|v\/|.*[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
      const match = insertData.url.match(youtubeRegex);
      if (match) {
        videoId = match[1];
      } else {
        toast.error('URL de YouTube no válida');
        return;
      }
      
      const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
      const videoUrl = `https://www.youtube.com/watch?v=${videoId}`;
      
      // HTML simple en una línea
      const html = `<a href="${videoUrl}" target="_blank"><img src="${thumbnailUrl}" alt="Ver video" style="max-width:100%;border-radius:8px;margin:10px 0;" /></a>`;
      insertAtCursor(html);
      setShowInsertModal(null);
      setInsertData({ url: '', text: '', alt: '' });
    };

    // Insertar link
    const handleInsertLink = () => {
      if (!insertData.url || !insertData.text) {
        toast.error('Ingresa la URL y el texto del enlace');
        return;
      }
      const html = `<a href="${insertData.url}" target="_blank" style="color: #6366f1; text-decoration: underline;">${insertData.text}</a>`;
      insertAtCursor(html);
      setShowInsertModal(null);
      setInsertData({ url: '', text: '', alt: '' });
    };

    // Insertar botón
    const handleInsertButton = () => {
      if (!insertData.url || !insertData.text) {
        toast.error('Ingresa la URL y el texto del botón');
        return;
      }
      const html = `
<div style="margin: 20px 0; text-align: center;">
  <a href="${insertData.url}" target="_blank" style="display: inline-block; background: linear-gradient(135deg, #6366f1, #8b5cf6); color: white; padding: 14px 32px; text-decoration: none; border-radius: 8px; font-weight: bold; font-size: 16px; box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);">${insertData.text}</a>
</div>`;
      insertAtCursor(html);
      setShowInsertModal(null);
      setInsertData({ url: '', text: '', alt: '' });
    };

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between p-4 border-b">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Mail className="h-5 w-5 text-purple-600" />
              {editingCampana ? 'Editar Campaña' : 'Nueva Campaña de Email'}
            </h2>
            <Button variant="ghost" size="sm" onClick={handleCerrar}>
              <X className="h-5 w-5" />
            </Button>
          </div>

          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nombre de la campaña *</Label>
                <Input
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  placeholder="Ej: Bienvenida Enero 2026"
                  required
                />
              </div>
              <div>
                <Label>Segmento de destinatarios</Label>
                <select
                  className="w-full p-2 border rounded-md"
                  value={formData.segmento}
                  onChange={(e) => setFormData({ ...formData, segmento: e.target.value })}
                >
                  <option value="todos">Todos los prospectos</option>
                  {ETAPAS.map(e => (
                    <option key={e.id} value={e.id}>{e.nombre}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <Label>Asunto del email *</Label>
              <Input
                value={formData.asunto}
                onChange={(e) => setFormData({ ...formData, asunto: e.target.value })}
                placeholder="Ej: ¡Bienvenido al Instituto Bíblico Apostólico!"
                required
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Contenido del email *</Label>
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => { setInsertData({ url: '', text: '', alt: '' }); setShowInsertModal('image'); }}
                    className="p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                    title="Insertar imagen"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => { setInsertData({ url: '', text: '', alt: '' }); setShowInsertModal('video'); }}
                    className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Insertar video de YouTube"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/>
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => { setInsertData({ url: '', text: '', alt: '' }); setShowInsertModal('link'); }}
                    className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Insertar enlace"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => { setInsertData({ url: '', text: '', alt: '' }); setShowInsertModal('button'); }}
                    className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                    title="Insertar botón"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                    </svg>
                  </button>
                </div>
              </div>
              
              <p className="text-xs text-gray-500 mb-2">
                Escribe tu mensaje. Usa **texto** para <strong>negrita</strong>. Variables: <code className="bg-yellow-100 px-1 rounded text-xs">{"{{nombre}}"}</code> <code className="bg-yellow-100 px-1 rounded text-xs">{"{{apellido}}"}</code>
              </p>
              
              <textarea
                ref={textareaRef}
                value={formData.contenido}
                onChange={(e) => setFormData({ ...formData, contenido: e.target.value })}
                placeholder={"Hola {{nombre}},\n\nTe escribimos del Instituto Bíblico Apostólico...\n\n**Importante:** No olvides inscribirte.\n\nPasos:\n1. Ingresa a la plataforma\n2. Ve a Registración\n3. Completa el formulario"}
                rows={8}
                className="w-full border rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none font-mono"
              />
              
              <div className="mt-3">
                <p className="text-xs text-gray-500 mb-1">👁️ Vista previa (así se verá en el email):</p>
                <div 
                  className="border rounded-lg p-4 bg-white text-sm min-h-[120px] max-h-[250px] overflow-auto shadow-inner"
                  style={{ lineHeight: '1.8' }}
                  dangerouslySetInnerHTML={{ 
                    __html: formData.contenido 
                      ? formData.contenido
                          // Variables
                          .replace(/\{\{nombre\}\}/g, '<strong style="color:#7c3aed">Juan</strong>')
                          .replace(/\{\{apellido\}\}/g, '<strong style="color:#7c3aed">Pérez</strong>')
                          // Negrita **texto**
                          .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
                          // Cursiva *texto*
                          .replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '<em>$1</em>')
                          // Doble salto = párrafo
                          .replace(/\n\s*\n/g, '</p><p style="margin: 15px 0;">')
                          // Salto simple = br
                          .replace(/\n/g, '<br/>')
                          // Envolver en párrafo
                          .replace(/^(.*)$/, '<p style="margin: 15px 0;">$1</p>')
                      : '<span style="color:#999">La vista previa aparecerá aquí...</span>'
                  }} 
                />
              </div>
            </div>

            {/* Sección de Votación */}
            <div className="border-t pt-4 mt-4">
              <div className="flex items-center gap-3 mb-3">
                <input
                  type="checkbox"
                  id="habilitar_votacion"
                  checked={formData.habilitar_votacion || false}
                  onChange={(e) => setFormData({ ...formData, habilitar_votacion: e.target.checked })}
                  className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                />
                <Label htmlFor="habilitar_votacion" className="text-sm font-medium cursor-pointer flex items-center gap-2">
                  📊 Habilitar votación (Sí/No) en este email
                </Label>
              </div>
              
              {formData.habilitar_votacion && (
                <div className="ml-7 p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <Label className="text-sm text-purple-700 mb-2 block">
                    Pregunta para la votación:
                  </Label>
                  <Input
                    value={formData.pregunta_votacion || ''}
                    onChange={(e) => setFormData({ ...formData, pregunta_votacion: e.target.value })}
                    placeholder="Ej: ¿Te gustaría inscribirte en este curso?"
                    className="border-purple-300 focus:ring-purple-500"
                  />
                  <p className="text-xs text-purple-600 mt-2">
                    ✨ Se agregarán botones de <span className="font-bold text-green-600">SÍ</span> y <span className="font-bold text-red-600">NO</span> al final del email
                  </p>
                </div>
              )}
            </div>

            <div className="flex gap-2 pt-4">
              <Button type="submit" className="flex-1 bg-purple-600 hover:bg-purple-700" disabled={saving}>
                {saving ? 'Guardando...' : (editingCampana ? 'Actualizar Campaña' : 'Crear Campaña')}
              </Button>
              <Button type="button" variant="outline" onClick={guardarBorrador} className="text-blue-600">
                <i className="fas fa-save mr-1"></i>
                Borrador
              </Button>
              <Button type="button" variant="outline" onClick={handleCerrar}>
                Cancelar
              </Button>
            </div>
            
            {/* Indicador de auto-guardado */}
            {lastSaved && (
              <p className="text-xs text-gray-400 text-center mt-2">
                <i className="fas fa-clock mr-1"></i>
                Último borrador guardado: {lastSaved.toLocaleTimeString()}
              </p>
            )}
          </form>

          {/* Mini-modal para insertar elementos */}
          {showInsertModal && (
            <div className="absolute inset-0 bg-black/30 flex items-center justify-center p-4" onClick={() => setShowInsertModal(null)}>
              <div className="bg-white rounded-xl shadow-xl p-4 w-full max-w-md" onClick={e => e.stopPropagation()}>
                <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                  {showInsertModal === 'image' && <><svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg> Insertar Imagen</>}
                  {showInsertModal === 'video' && <><svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 24 24"><path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/></svg> Insertar Video de YouTube</>}
                  {showInsertModal === 'link' && <><svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg> Insertar Enlace</>}
                  {showInsertModal === 'button' && <><svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" /></svg> Insertar Botón</>}
                </h3>

                <div className="space-y-3">
                  <div>
                    <Label>{showInsertModal === 'video' ? 'URL del video de YouTube' : 'URL'}</Label>
                    <Input
                      value={insertData.url}
                      onChange={(e) => setInsertData({ ...insertData, url: e.target.value })}
                      placeholder={
                        showInsertModal === 'image' ? 'https://ejemplo.com/imagen.jpg' :
                        showInsertModal === 'video' ? 'https://www.youtube.com/watch?v=...' :
                        'https://ejemplo.com/pagina'
                      }
                    />
                  </div>

                  {showInsertModal === 'image' && (
                    <div>
                      <Label>Texto alternativo (opcional)</Label>
                      <Input
                        value={insertData.alt}
                        onChange={(e) => setInsertData({ ...insertData, alt: e.target.value })}
                        placeholder="Descripción de la imagen"
                      />
                    </div>
                  )}

                  {(showInsertModal === 'link' || showInsertModal === 'button') && (
                    <div>
                      <Label>Texto a mostrar</Label>
                      <Input
                        value={insertData.text}
                        onChange={(e) => setInsertData({ ...insertData, text: e.target.value })}
                        placeholder={showInsertModal === 'button' ? 'Ej: Ver más información' : 'Ej: Haz clic aquí'}
                      />
                    </div>
                  )}
                </div>

                <div className="flex gap-2 mt-4">
                  <Button
                    type="button"
                    className="flex-1 bg-purple-600 hover:bg-purple-700"
                    onClick={() => {
                      if (showInsertModal === 'image') handleInsertImage();
                      else if (showInsertModal === 'video') handleInsertVideo();
                      else if (showInsertModal === 'link') handleInsertLink();
                      else if (showInsertModal === 'button') handleInsertButton();
                    }}
                  >
                    Insertar
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowInsertModal(null)}>
                    Cancelar
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
  
  // Modal de Importar CSV
  function ImportCSVModal() {
    const [file, setFile] = useState(null);
    const [etapaDefault, setEtapaDefault] = useState('interesado');
    const [importing, setImporting] = useState(false);
    const [resultado, setResultado] = useState(null);

    const handleFileChange = (e) => {
      const selectedFile = e.target.files[0];
      if (selectedFile && selectedFile.name.endsWith('.csv')) {
        setFile(selectedFile);
        setResultado(null);
      } else {
        toast.error('Por favor selecciona un archivo CSV');
      }
    };

    const handleImport = async () => {
      if (!file) {
        toast.error('Selecciona un archivo CSV primero');
        return;
      }

      setImporting(true);
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('etapa_default', etapaDefault);
        formData.append('fuente_default', 'importacion');

        const response = await axios.post(`${API}/marketing/importar-csv`, formData, {
          headers: { 
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'multipart/form-data'
          }
        });

        if (response.data.success) {
          setResultado(response.data);
          if (response.data.creados > 0) {
            toast.success(`${response.data.creados} prospectos importados`);
            loadData(); // Recargar datos
          }
        }
      } catch (error) {
        console.error('Error importando CSV:', error);
        toast.error(error.response?.data?.detail || 'Error al importar CSV');
      } finally {
        setImporting(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
          <div className="flex items-center justify-between p-4 border-b">
            <div className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-purple-600" />
              <h2 className="text-lg font-bold">Importar Prospectos desde CSV</h2>
            </div>
            <Button variant="ghost" size="sm" onClick={() => { setShowImportModal(false); setResultado(null); setFile(null); }}>
              <X className="h-5 w-5" />
            </Button>
          </div>

          <div className="p-4 space-y-4">
            {!resultado ? (
              <>
                {/* Info sobre formato */}
                <div className="bg-blue-50 rounded-lg p-3 text-sm">
                  <p className="font-medium text-blue-800 mb-1">Formato del CSV:</p>
                  <p className="text-blue-700">Columnas aceptadas: nombre, apellido, email, telefono, notas</p>
                  <p className="text-blue-600 text-xs mt-1">Compatible con exportaciones de Wix, Google Contacts, etc.</p>
                </div>

                {/* Selector de archivo */}
                <div>
                  <Label>Archivo CSV</Label>
                  <div className="mt-1">
                    <label className="flex items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-purple-400 hover:bg-purple-50 transition-colors">
                      <div className="text-center">
                        {file ? (
                          <>
                            <FileSpreadsheet className="h-8 w-8 mx-auto text-purple-600 mb-2" />
                            <p className="text-sm font-medium text-gray-700">{file.name}</p>
                            <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
                          </>
                        ) : (
                          <>
                            <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                            <p className="text-sm text-gray-600">Click para seleccionar CSV</p>
                            <p className="text-xs text-gray-400">o arrastra el archivo aquí</p>
                          </>
                        )}
                      </div>
                      <input
                        type="file"
                        accept=".csv"
                        onChange={handleFileChange}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>

                {/* Etapa inicial */}
                <div>
                  <Label>Etapa inicial para los prospectos</Label>
                  <select
                    className="w-full p-2 border rounded-md mt-1"
                    value={etapaDefault}
                    onChange={(e) => setEtapaDefault(e.target.value)}
                  >
                    {ETAPAS.map(e => (
                      <option key={e.id} value={e.id}>{e.nombre}</option>
                    ))}
                  </select>
                </div>

                {/* Botones */}
                <div className="flex gap-2 pt-4">
                  <Button 
                    onClick={handleImport} 
                    className="flex-1 bg-purple-600 hover:bg-purple-700" 
                    disabled={!file || importing}
                  >
                    {importing ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Importando...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" /> Importar
                      </>
                    )}
                  </Button>
                  <Button variant="outline" onClick={() => { setShowImportModal(false); setFile(null); }}>
                    Cancelar
                  </Button>
                </div>
              </>
            ) : (
              /* Resultado de la importación */
              <div className="space-y-4">
                <div className="text-center py-4">
                  <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-2" />
                  <h3 className="text-lg font-bold">Importación Completada</h3>
                </div>

                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="bg-green-50 rounded-lg p-3">
                    <p className="text-2xl font-bold text-green-600">{resultado.creados}</p>
                    <p className="text-xs text-green-700">Creados</p>
                  </div>
                  <div className="bg-yellow-50 rounded-lg p-3">
                    <p className="text-2xl font-bold text-yellow-600">{resultado.duplicados}</p>
                    <p className="text-xs text-yellow-700">Duplicados</p>
                  </div>
                  <div className="bg-red-50 rounded-lg p-3">
                    <p className="text-2xl font-bold text-red-600">{resultado.total_errores}</p>
                    <p className="text-xs text-red-700">Errores</p>
                  </div>
                </div>

                {resultado.errores && resultado.errores.length > 0 && (
                  <div className="bg-red-50 rounded-lg p-3">
                    <p className="text-sm font-medium text-red-800 mb-1">Errores encontrados:</p>
                    <ul className="text-xs text-red-700 list-disc list-inside max-h-32 overflow-y-auto">
                      {resultado.errores.map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <Button 
                  onClick={() => { setShowImportModal(false); setResultado(null); setFile(null); }} 
                  className="w-full bg-purple-600 hover:bg-purple-700"
                >
                  Cerrar
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
    );
  }
};

export default Marketing;
